import { useEffect, useState } from "react";
import type { DenialPage, DenialRecord, OperationSpec } from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button } from "./components";
type Page = DenialPage & { healthy: boolean };
export function SecurityAudit() {
  const { api } = useWorkspaceTransport();
  const [records, setRecords] = useState<DenialRecord[]>([]),
    [cursor, setCursor] = useState<string>(),
    [healthy, setHealthy] = useState(true),
    [policy, setPolicy] = useState<OperationSpec[]>([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function load(before?: string) {
    setBusy(true);
    setError("");
    try {
      const page = await api<Page>(
        `/security-audit?limit=50${before ? `&before=${encodeURIComponent(before)}` : ""}`,
      );
      setRecords((current) =>
        before
          ? [
              ...current,
              ...page.records.filter(
                (r) => !current.some((v) => v.id === r.id),
              ),
            ]
          : page.records,
      );
      setCursor(page.nextCursor);
      setHealthy(page.healthy);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load();
    void api<{ operations: OperationSpec[] }>("/operation-policy")
      .then((v) => setPolicy(v.operations))
      .catch((e) => setError(e.message));
  }, []);
  return (
    <section className="tenant-access security-audit">
      <span className="eyebrow">WORKSPACE</span>
      <h1>Security audit</h1>
      <p>
        Authorization denials identify the operation and required access without
        retaining request contents.
      </p>
      {error && <p role="alert">{error}</p>}
      {!healthy && (
        <p role="alert">
          Some denial events could not be written. Check the audit store.
        </p>
      )}
      <Button disabled={busy} onClick={() => void load()}>
        Refresh security audit
      </Button>
      {!busy && !records.length && (
        <p>No authorization denials are recorded for this tenant.</p>
      )}
      <ol className="access-audit">
        {records.map((record) => (
          <li key={record.id} data-denial-id={record.id}>
            <strong>{record.operation}</strong>
            <p>
              {record.reason} · {new Date(record.at).toLocaleString()}
            </p>
            <p>
              Actor: {record.actorId}
              {record.effectiveSubjectId !== record.actorId
                ? ` · Acting as: ${record.effectiveSubjectId}`
                : ""}
            </p>
            <p>
              Required:{" "}
              {record.required
                .map((group) => group.join(" or "))
                .join(" and ") || "Authenticated tenant context"}
            </p>
          </li>
        ))}
      </ol>
      {cursor && (
        <Button disabled={busy} onClick={() => void load(cursor)}>
          Older denial events
        </Button>
      )}
      <details>
        <summary>Operation policy</summary>
        <p>
          API ceiling: 1,000 requests per minute per client address. Protected
          mutations allow 60 and artifact reads allow 120 per operation.
        </p>
        <div className="policy-scroll">
          <table>
            <thead>
              <tr>
                <th>Operation</th>
                <th>Route</th>
                <th>Required access</th>
                <th>Limit/minute</th>
              </tr>
            </thead>
            <tbody>
              {policy.map((op) => (
                <tr key={op.id}>
                  <td>{op.id}</td>
                  <td>
                    {op.method} {op.path}
                  </td>
                  <td>
                    {op.required
                      .map((group) => group.join(" or "))
                      .join(" and ") || "Authenticated tenant context"}
                    {op.approvalRequiresRoles
                      ? "; approval also needs role management"
                      : ""}
                    {op.probationRequiresOperations
                      ? "; probation also needs render operations"
                      : ""}
                  </td>
                  <td>
                    {op.limit === "artifact"
                      ? 120
                      : op.limit === "mutation"
                        ? 60
                        : "Global ceiling"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
